import type { PageContextService } from './services/PageContextService';
import type { TabService } from './services/TabService';
import type { CustomerService } from './services/CustomerService';
import type { DeviceService } from './services/DeviceService';
import type { MeasurementSnapshot } from './services/MeasurementStore';

/** Wire format for one logical payload row inside a batched collect POST (see {@link ApiClient}). */
export interface HeartbeatPayload {
  meta: { version: number };
  page: {
    type: 'product-detail';
    url: string;
    utm_campaign: string | null;
    utm_source: string | null;
    utm_medium: string | null;
  };
  pageData: {
    product_id: string;
    variant_id: string | null;
    price: number;
  };
  user: {
    device_id: string;
    tab_id: string;
    customer_id: string | null;
    device_type: 'mobile' | 'tablet' | 'desktop';
    os: string | null;
    browser: string | null;
  };
  measurements: MeasurementSnapshot;
  heartbeat: number;
  heartbeat_index: number;
}

/**
 * Fluent assembler for {@link HeartbeatPayload}. **`build()` is async** because resolving `device_id` may
 * load FingerprintJS on first run — callers should await before enqueueing.
 */
export class PayloadBuilder {
  private version: number | null = null;
  private pageContextService: PageContextService | null = null;

  private tabService: TabService | null = null;
  private customerService: CustomerService | null = null;
  private deviceService: DeviceService | null = null;

  private measurements: MeasurementSnapshot | null = null;

  private heartbeatMs: number | null = null;
  private heartbeatIndex: number | null = null;

  withMeta(version: number): this {
    this.version = version;

    return this;
  }

  withPage(pageContextService: PageContextService): this {
    this.pageContextService = pageContextService;

    return this;
  }

  withUser(
    tab: TabService,
    customer: CustomerService,
    device: DeviceService
  ): this {
    this.tabService = tab;
    this.customerService = customer;
    this.deviceService = device;

    return this;
  }

  withMeasurements(measurements: MeasurementSnapshot): this {
    this.measurements = measurements;

    return this;
  }

  withHeartbeat(
    intervalMs: number,
    heartbeatIndex: number
  ): this {
    this.heartbeatMs = intervalMs;
    this.heartbeatIndex = heartbeatIndex;

    return this;
  }

  async build(): Promise<HeartbeatPayload> {
    if (this.version == null) {
      throw new Error('PayloadBuilder: meta.version is required');
    }
    if (!this.pageContextService) {
      throw new Error('PayloadBuilder: page context is required');
    }
    if (!this.tabService || !this.customerService || !this.deviceService) {
      throw new Error('PayloadBuilder: user services are required');
    }
    if (!this.measurements) {
      throw new Error('PayloadBuilder: measurements are required');
    }
    if (this.heartbeatMs == null || this.heartbeatIndex == null) {
      throw new Error('PayloadBuilder: heartbeat info is required');
    }

    const ctx = this.pageContextService.get();
    const deviceId = await this.deviceService.getDeviceId();

    return {
      meta: { version: this.version },
      page: ctx.page,
      pageData: ctx.pageData,
      user: {
        device_id: deviceId,
        tab_id: this.tabService.getTabId(),
        customer_id: this.customerService.getCustomerId(),
        device_type: ctx.device_type,
        os: ctx.os,
        browser: ctx.browser,
      },
      measurements: this.measurements,
      heartbeat: this.heartbeatMs,
      heartbeat_index: this.heartbeatIndex,
    };
  }
}
